import React from 'react';
import { H } from './quantum/gates';
import { zeroState, applyGate, probabilities, blochVectorForQubit } from './quantum/QuantumState';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';

// Real states, not decorative fakes — reused from the same engine every
// other page builds on, so what's taught here matches what students see
// when they go try it themselves.
const zero = zeroState(1);
const plus = applyGate(zeroState(1), H, 0, 1);

const zeroVec = blochVectorForQubit(zero, 1, 0);
const plusVec = blochVectorForQubit(plus, 1, 0);
const plusProbs = probabilities(plus);
const mixedVec = { x: 0, y: 0, z: 0 };

// A purely illustrative "could be anywhere" vector for the intro card —
// still a genuine unit Bloch vector (sin(theta)cos(phi), sin(theta)sin(phi), cos(theta)),
// just not tied to one specific named state.
const genericTheta = (50 * Math.PI) / 180;
const genericPhi = (35 * Math.PI) / 180;
const genericVec = {
  x: Math.sin(genericTheta) * Math.cos(genericPhi),
  y: Math.sin(genericTheta) * Math.sin(genericPhi),
  z: Math.cos(genericTheta),
};

function TryItButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
    >
      {children} →
    </button>
  );
}

export default function LearnPage({ onNavigate }) {
  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-3">
          📚 Quantum Computing for New Learners
        </h2>
        <p className="text-gray-600 text-lg">
          Essential concepts to understand before exploring the visualizations
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Qubit Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all">
          <h3 className="text-2xl font-bold text-blue-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">⚛️</span> What is a Qubit?
          </h3>
          <div className="flex gap-4 items-center mb-4">
            <BlochSphereCanvas vector={genericVec} size={110} showMixed={false} />
            <p className="text-gray-600 text-sm">
              A quantum bit (qubit) is the quantum version of a classical bit. Unlike classical bits that are either 0 or 1, a qubit's state can be any point on this sphere.
            </p>
          </div>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ Can be |0⟩ (read as "ket zero")</li>
            <li>✓ Can be |1⟩ (read as "ket one")</li>
            <li>✓ Can be both |0⟩ and |1⟩ at the same time (superposition)</li>
          </ul>
          <TryItButton onClick={() => onNavigate('single-qubit')}>Try it yourself</TryItButton>
        </div>

        {/* Bloch Sphere Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-purple-300 transition-all">
          <h3 className="text-2xl font-bold text-purple-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">🌐</span> Bloch Sphere
          </h3>
          <div className="flex gap-4 items-center mb-4">
            <BlochSphereCanvas vector={plusVec} size={110} showMixed={false} />
            <p className="text-gray-600 text-sm">
              The Bloch sphere is a geometric representation of a single qubit's state. This example shows the equator state |+⟩ — an even mix of |0⟩ and |1⟩.
            </p>
          </div>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ North pole (↑) represents |0⟩</li>
            <li>✓ South pole (↓) represents |1⟩</li>
            <li>✓ Equator represents superposition states</li>
          </ul>
          <TryItButton onClick={() => onNavigate('gates-playground')}>Try it yourself</TryItButton>
        </div>

        {/* Superposition Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all">
          <h3 className="text-2xl font-bold text-teal-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">🌊</span> Superposition
          </h3>
          <p className="text-gray-600 mb-3 text-sm">
            Superposition is when a qubit exists in multiple states at once. When measured, it "collapses" into either 0 or 1.
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4 max-w-[220px]">
            <div className="bg-cyan-50 rounded p-2 border border-cyan-100 text-center">
              <div className="text-cyan-600 text-xs">P(|0⟩)</div>
              <div className="text-cyan-700 font-bold">{(plusProbs[0] * 100).toFixed(0)}%</div>
            </div>
            <div className="bg-pink-50 rounded p-2 border border-pink-100 text-center">
              <div className="text-pink-600 text-xs">P(|1⟩)</div>
              <div className="text-pink-700 font-bold">{(plusProbs[1] * 100).toFixed(0)}%</div>
            </div>
          </div>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ |ψ⟩ = α|0⟩ + β|1⟩ (mathematical notation)</li>
            <li>✓ α and β are probability amplitudes</li>
            <li>✓ Measurement gives 0 or 1 based on probabilities</li>
          </ul>
          <TryItButton onClick={() => onNavigate('single-qubit')}>Try it yourself</TryItButton>
        </div>

        {/* Measurement Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all">
          <h3 className="text-2xl font-bold text-orange-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">🔍</span> Measurement
          </h3>
          <div className="flex gap-3 items-center justify-center mb-4">
            <div className="text-center">
              <BlochSphereCanvas vector={plusVec} size={95} showMixed={false} />
              <div className="text-xs text-gray-500 mt-1">Before</div>
            </div>
            <div className="text-2xl text-gray-300">→</div>
            <div className="text-center">
              <BlochSphereCanvas vector={zeroVec} size={95} showMixed={false} />
              <div className="text-xs text-gray-500 mt-1">After (example)</div>
            </div>
          </div>
          <p className="text-gray-600 mb-3 text-sm">
            Measuring a qubit forces it to "choose" between 0 or 1. The probability depends on its quantum state before measurement.
          </p>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ Before measurement: superposition exists</li>
            <li>✓ After measurement: definite 0 or 1</li>
            <li>✓ Irreversible process (state collapses <em>with respect to the measurement basis</em> — not "destroyed information" in general, just resolved into a definite outcome)</li>
          </ul>
          <TryItButton onClick={() => onNavigate('single-qubit')}>Try it yourself</TryItButton>
        </div>

        {/* Entanglement Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-pink-300 transition-all">
          <h3 className="text-2xl font-bold text-pink-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">🔗</span> Entanglement
          </h3>
          <div className="flex gap-3 items-center justify-center mb-4">
            <BlochSphereCanvas vector={mixedVec} size={95} showMixed />
            <div className="text-2xl text-pink-400">⟷</div>
            <BlochSphereCanvas vector={mixedVec} size={95} showMixed />
          </div>
          <p className="text-gray-600 mb-3 text-sm">
            Entanglement occurs when two or more qubits become correlated. Alone, each entangled qubit has no individual definite state — that's what "mixed" means above. Measuring
            one instantly determines the other, even if far apart!
          </p>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ Qubits share a quantum state</li>
            <li>✓ Measurement outcomes are correlated</li>
            <li>✓ Einstein called this "spooky action at a distance"</li>
          </ul>
          <TryItButton onClick={() => onNavigate('entanglement')}>Try it yourself</TryItButton>
        </div>

        {/* Tips Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-amber-300 transition-all">
          <h3 className="text-2xl font-bold text-amber-700 mb-3 flex items-center">
            <span className="text-3xl mr-2">💡</span> Tips for Learning
          </h3>
          <p className="text-gray-600 mb-4">
            Make the most of the interactive visualizations:
          </p>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li>✓ Start with Single Qubit tab to understand basics</li>
            <li>✓ Adjust sliders and observe the Bloch sphere</li>
            <li>✓ Try the Gates Playground to see how X, H, Z, etc. move the state</li>
            <li>✓ Then explore Entanglement and Teleportation for multi-qubit concepts</li>
          </ul>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="text-center mt-12 space-y-4">
        <p className="text-gray-700 text-lg font-semibold">Ready to explore?</p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={() => onNavigate('single-qubit')}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all transform hover:scale-105 shadow-md"
          >
            Start with Single Qubit
          </button>
          <button
            onClick={() => onNavigate('entanglement')}
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all transform hover:scale-105 shadow-md"
          >
            Explore Entanglement
          </button>
        </div>
      </div>
    </div>
  );
}
